import React, { useState } from 'react';
import { X, Download, Briefcase } from 'lucide-react';
import { YTDSummaryCard } from './canadian/YTDSummaryCard';
import { VacationBalanceCard } from './canadian/VacationBalanceCard';
import { CanadianPayslipPdfButton } from './canadian/CanadianPayslipPdfButton';
import { useCanadianPayroll } from '../context/CanadianPayrollContext';
import { useAuth } from '../context/AuthContext';

const CURRENCY_SYMBOLS_V = {
  USD: '$', CAD: '$', AUD: 'A$', NZD: 'NZ$', SGD: 'S$', HKD: 'HK$',
  EUR: '€', GBP: '£', PHP: '₱', INR: '₹', BRL: 'R$', MXN: '$', JPY: '¥',
};

export default function PayslipView({ payslip, onClose, currency = 'USD', brandName = '', location = '', logoUrl = '' }) {
  const [downloading, setDownloading] = useState(false);
  const { caEnabled } = useCanadianPayroll();
  const { user, canManage } = useAuth();

  const fmt = (n) => {
    if (n == null) return '—';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n));
    } catch {
      const sym = CURRENCY_SYMBOLS_V[currency] || currency + ' ';
      return `${sym}${Number(n).toFixed(2)}`;
    }
  };

  const currencySymbol = CURRENCY_SYMBOLS_V[currency] || currency + ' ';

  if (!payslip) return null;

  const earnings   = (payslip.items || []).filter(i => i.type === 'earning');
  const deductions = (payslip.items || []).filter(i => i.type === 'deduction');

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const { default: jsPDF }    = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Header — optional logo (top-right corner)
      let yCursor = 22;
      if (logoUrl && /^https?:\/\//i.test(logoUrl)) {
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                const maxW = 240, maxH = 80;
                let w = img.naturalWidth, h = img.naturalHeight;
                const scale = Math.min(maxW / w, maxH / h, 1);
                w = Math.round(w * scale); h = Math.round(h * scale);
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                resolve({ url: canvas.toDataURL('image/png'), w, h });
              } catch (err) { reject(err); }
            };
            img.onerror = () => reject(new Error('logo load failed'));
            img.src = logoUrl;
          });
          const mmW = Math.min(40, dataUrl.w / 3.78);
          const mmH = Math.min(16, dataUrl.h / 3.78);
          doc.addImage(dataUrl.url, 'PNG', 210 - 14 - mmW, 12, mmW, mmH);
        } catch (_err) {
          // silent fallback — continue without logo
        }
      }
      if (brandName) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text(brandName, 14, yCursor);
        yCursor += 6;
      }
      if (location) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(location, 14, yCursor);
        yCursor += 6;
      }
      // Reset color and add Payslip title
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Payslip', 14, yCursor + 4);
      yCursor += 12;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Employee: ${payslip.employee_name}`, 14, yCursor);
      doc.text(`Email: ${payslip.employee_email || '—'}`, 14, yCursor + 6);
      doc.text(`Period: ${payslip.period_start}  →  ${payslip.period_end}`, 14, yCursor + 12);
      doc.text(`Hourly Rate: ${payslip.hourly_wage != null ? `${currencySymbol}${payslip.hourly_wage}/hr` : '—'}`, 14, yCursor + 18);

      // Hours summary
      autoTable(doc, {
        startY: yCursor + 26,
        head: [['Hours Type', 'Hours']],
        body: [
          ['Regular Hours',   `${payslip.regular_hours}h`],
          ['Overtime Hours',  `${payslip.overtime_hours}h`],
          ['Holiday Hours',   `${payslip.holiday_hours}h`],
          ['Break Hours',     `${payslip.break_hours}h`],
          ['Total Hours',     `${payslip.total_hours}h`],
        ],
        theme: 'grid',
        headStyles: { fillColor: [241, 245, 249] , textColor: [30, 41, 59], fontStyle: 'bold' },
        styles: { fontSize: 9 },
        columnStyles: { 1: { halign: 'right' } },
      });

      // Earnings
      if (earnings.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 8,
          head: [['Earnings', 'Hours', 'Amount']],
          body: earnings.map(i => [i.name, i.hours != null ? `${i.hours}h` : '—', fmt(i.amount)]),
          theme: 'grid',
          headStyles: { fillColor: [209, 250, 229], textColor: [22, 101, 52], fontStyle: 'bold' },
          styles: { fontSize: 9 },
          columnStyles: { 2: { halign: 'right' } },
        });
      }

      // Deductions
      if (deductions.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 8,
          head: [['Deductions', 'Amount']],
          body: deductions.map(i => [i.name, fmt(i.amount)]),
          theme: 'grid',
          headStyles: { fillColor: [254, 226, 226], textColor: [153, 27, 27], fontStyle: 'bold' },
          styles: { fontSize: 9 },
          columnStyles: { 1: { halign: 'right' } },
        });
      }

      // Totals
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        body: [
          ['Gross Pay', fmt(payslip.gross_pay)],
          ['Total Deductions', `(${fmt(payslip.total_deductions)})`],
          ['Net Pay', fmt(payslip.net_pay)],
        ],
        theme: 'plain',
        styles: { fontSize: 10, fontStyle: 'bold' },
        columnStyles: { 1: { halign: 'right' } },
      });

      doc.save(`payslip_${payslip.employee_name.replace(/\s/g, '_')}_${payslip.period_start}.pdf`);
    } catch (err) {
      console.error('PDF error:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="payslip-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && /^https?:\/\//i.test(logoUrl) ? (
              <img
                src={logoUrl}
                alt={(brandName || 'Logo') + ''}
                className="h-9 max-w-[100px] object-contain shrink-0"
                data-testid="payslip-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <Briefcase size={18} className="text-slate-500 shrink-0" />
            )}
            <div className="min-w-0">
              {(brandName || location) && (
                <p className="text-[11px] text-slate-400 truncate" data-testid="payslip-brand-line">
                  {brandName}{brandName && location ? ' · ' : ''}{location}
                </p>
              )}
              <p className="font-semibold text-slate-800 text-sm truncate" data-testid="payslip-employee-name">
                {payslip.employee_name}
              </p>
              <p className="text-xs text-slate-400">
                {payslip.period_start} → {payslip.period_end}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              data-testid="payslip-download-pdf-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50"
            >
              <Download size={13} />
              {downloading ? 'Generating…' : 'PDF'}
            </button>
            {caEnabled && (
              <CanadianPayslipPdfButton
                payslipId={payslip.id}
                employeeName={payslip.employee_name}
                periodStart={payslip.period_start}
                className="text-xs px-3 py-1.5"
              />
            )}
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">

          {/* Hours summary */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Hours</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Regular', value: `${payslip.regular_hours}h` },
                { label: 'Overtime', value: `${payslip.overtime_hours}h` },
                { label: 'Holiday', value: `${payslip.holiday_hours}h` },
              ].map(({ label, value }) => (
                <div key={label} className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className="text-sm font-semibold text-slate-700">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Earnings */}
          {earnings.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Earnings</p>
              <div className="rounded-lg border border-slate-100 divide-y divide-slate-100">
                {earnings.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-2.5">
                    <div>
                      <p className="text-sm text-slate-700">{item.name}</p>
                      {item.hours != null && (
                        <p className="text-xs text-slate-400">{item.hours}h</p>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-teal-600">{fmt(item.amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deductions */}
          {deductions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Deductions</p>
              <div className="rounded-lg border border-slate-100 divide-y divide-slate-100">
                {deductions.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-2.5">
                    <p className="text-sm text-slate-700">{item.name}</p>
                    <p className="text-sm font-semibold text-red-500">({fmt(item.amount)})</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            <div className="flex justify-between px-4 py-2.5">
              <p className="text-sm text-slate-600">Gross Pay</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="payslip-gross-pay">{fmt(payslip.gross_pay)}</p>
            </div>
            {payslip.total_deductions > 0 && (
              <div className="flex justify-between px-4 py-2.5">
                <p className="text-sm text-slate-600">Deductions</p>
                <p className="text-sm font-semibold text-red-500">({fmt(payslip.total_deductions)})</p>
              </div>
            )}
            <div className="flex justify-between px-4 py-3 bg-slate-50 rounded-b-lg">
              <p className="text-sm font-bold text-slate-800">Net Pay</p>
              <p className="text-base font-bold text-slate-900" data-testid="payslip-net-pay">{fmt(payslip.net_pay)}</p>
            </div>
          </div>

          {payslip.hourly_wage == null && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              No hourly rate set for this employee — pay amounts not calculated.
            </p>
          )}

          {/* Canadian Payroll sections — additive, country-gated at API layer */}
          {caEnabled && payslip.employee_id && (
            <>
              <YTDSummaryCard
                employeeId={payslip.employee_id}
                year={(payslip.period_start || '').slice(0, 4) ? parseInt((payslip.period_start || '').slice(0, 4), 10) : undefined}
              />
              <VacationBalanceCard
                employeeId={payslip.employee_id}
                canEdit={!!canManage || user?.id === payslip.employee_id ? !!canManage : false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
